// src/components/fraud/FraudSystemHealth.js
// System Health widget — small status panel showing:
//   • Fraud Engine Status
//   • Last fraud_config update
//   • Firestore connected
//   • Stripe connected

import React from 'react';

// ─── Status indicator dot ─────────────────────────────────────────────────────
const StatusDot = ({ status }) => {
  // status: true | false | null | 'Active' | 'Unknown' | etc.
  let color = '#A6A3B8'; // unknown/null
  let pulse = false;
  if (status === true || status === 'Active' || status === 'active') {
    color = '#22C55E'; pulse = true;
  } else if (status === false || status === 'Inactive' || status === 'inactive') {
    color = '#EF4444';
  } else if (status === 'Warning' || status === 'warning') {
    color = '#F59E0B'; pulse = true;
  }
  return (
    <span
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: color,
        boxShadow: pulse ? `0 0 0 2px ${color}44` : 'none',
        flexShrink: 0,
        animation: pulse ? 'fraudPulse 2s infinite' : 'none',
      }}
    />
  );
};

// ─── Health row ───────────────────────────────────────────────────────────────
const HealthRow = ({ label, value, status, loading }) => (
  <div
    className="d-flex align-items-center justify-content-between py-2"
    style={{ borderBottom: '1px solid var(--divider)' }}
  >
    <div className="d-flex align-items-center gap-2">
      <StatusDot status={status} />
      <small style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{label}</small>
    </div>
    {loading ? (
      <div className="fraud-skeleton" style={{ width: 80, height: 10, borderRadius: 4 }} />
    ) : (
      <small
        style={{
          fontSize: 12,
          fontWeight: 600,
          color:
            status === true || status === 'Active' || status === 'active'
              ? '#22C55E'
              : status === false || status === 'Inactive'
              ? '#EF4444'
              : 'var(--text-primary)',
          maxWidth: 140,
          textAlign: 'right',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </small>
    )}
  </div>
);

// ─── Format date for health display ─────────────────────────────────────────
const fmtHealthDate = (d) => {
  if (!d) return 'Unknown';
  const date = d instanceof Date ? d : new Date(d);
  return isNaN(date) ? 'Unknown' : date.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FraudSystemHealth = ({ health, loading }) => {
  const { engineStatus, lastConfigUpdate, firestoreConnected, stripeConnected } = health;

  return (
    <div id="fraud-system-health" className="theme-card rounded-3 p-4 h-100">
      <div className="d-flex align-items-center gap-2 mb-3">
        <span style={{ fontSize: 16 }}>⚙️</span>
        <h6 className="mb-0 text-h2" style={{ fontSize: 15 }}>
          System Health
        </h6>
      </div>

      <HealthRow
        label="Fraud Engine"
        value={loading ? '' : (engineStatus || 'Unknown')}
        status={engineStatus === 'Active' || engineStatus === 'active' ? true : engineStatus ? engineStatus : null}
        loading={loading}
      />
      <HealthRow
        label="Last Config Update"
        value={loading ? '' : fmtHealthDate(lastConfigUpdate)}
        status={lastConfigUpdate ? 'Active' : null}
        loading={loading}
      />
      <HealthRow
        label="Firestore"
        value={loading ? '' : firestoreConnected ? 'Connected' : firestoreConnected === false ? 'Disconnected' : 'Unknown'}
        status={firestoreConnected === true ? true : firestoreConnected === false ? false : null}
        loading={loading}
      />
      {/* <HealthRow
        label="Stripe"
        value={loading ? '' : stripeConnected === true ? 'Connected' : stripeConnected === false ? 'Disconnected' : 'Not configured'}
        status={stripeConnected === true ? true : stripeConnected === false ? false : null}
        loading={loading}
      /> */}

      {/* Inline pulse animation */}
      <style>{`
        @keyframes fraudPulse {
          0%, 100% { box-shadow: 0 0 0 2px rgba(34,197,94,0.4); }
          50% { box-shadow: 0 0 0 5px rgba(34,197,94,0.1); }
        }
      `}</style>
    </div>
  );
};

export default FraudSystemHealth;
